import { forwardRef } from 'react'
import numberFormat from 'number-format.js'
import {
  PriceWrapper,
  PriceLabel,
  DiscountLabel,
  IconWrapper,
  UnitPriceLabel,
  FeeLabel
} from './Price.styles'
import { PriceProps } from './Price.types'
import { isString } from '../../../utils'
import { getUnitPrice, getDisplayContent } from './Price.utils'

const Price = forwardRef<HTMLElement, PriceProps>(
  (
    {
      className,
      styles,
      value = 0,
      discount,
      format = '#,##0.00',
      currency = '',
      prefix = '',
      suffix = '',
      symbol = '',
      code = '',
      inline = false,
      color,
      size = 'inherit',
      display = '{{symbol}}{{price}}{{code}}',
      discountColor = 'text.secondary',
      discountSize = '0.85em',
      icon = 'MdLocalOffer',
      showIcon = false,
      validate = true,
      showUnitPrice = false,
      unitPrice = 0,
      units = 1,
      unitsDisplay = '({{symbol}}{{price}})',
      unitsSize = '0.85em',
      unitsColor = 'text.secondary',
      unitsInline = false,
      wrapContent = false,
      fee = 0,
      feeDisplay = '{{symbol}}{{price}}',
      feeSize = '0.75em',
      feeColor = 'text.secondary',
      feeAlways = false,
      priceOrder = 1,
      discountOrder = 2,
      unitOrder = 3,
      ...props
    },
    ref
  ) => {
    const baseClasses = className ? `Price ${className}` : 'Price'
    const iconProps = isString(icon) ? { name: icon } : icon
    const hasDeprecatedProps = !!suffix || !!prefix
    const priceValue =
      typeof discount === 'number' && discount < value ? discount : value
    const discountValue =
      discount !== undefined && (value > discount || !validate) ? value : 0
    const unitPriceValue = getUnitPrice(
      unitPrice,
      showUnitPrice,
      priceValue,
      units
    )

    const displayArgs = { display, code, symbol: symbol || currency, format }
    const [priceFormatted, prefixContent, suffixContent] = hasDeprecatedProps
      ? [numberFormat(format, value), `${prefix} ${currency}`, suffix]
      : getDisplayContent({
          ...displayArgs,
          value: priceValue
        })
    const [discountFormatted] = getDisplayContent({
      ...displayArgs,
      value: discountValue
    })
    const [unitsFormatted, unitsPrefix, unitsSuffix] = showUnitPrice
      ? getDisplayContent({
          ...displayArgs,
          display: unitsDisplay,
          value: unitPriceValue
        })
      : ['', '', '']
    const [feeFormatted, feePrefix, feeSuffix] = getDisplayContent({
      ...displayArgs,
      display: feeDisplay,
      value: fee
    })

    return (
      <PriceWrapper
        className={baseClasses}
        wrapContent={wrapContent}
        inline={inline}
        sx={styles}
        ref={ref}
        size={size}
        {...props}>
        <PriceLabel
          className="PriceLabel"
          order={priceOrder}
          color={color}
          size={size}>
          {icon && showIcon && <IconWrapper color={color} {...iconProps} />}
          {`${prefixContent}${priceFormatted}${suffixContent}`}
          {fee || feeAlways ? (
            <FeeLabel className="PriceFee" size={feeSize} color={feeColor}>
              {`${feePrefix}${feeFormatted}${feeSuffix}`}
            </FeeLabel>
          ) : null}
        </PriceLabel>
        {discountValue ? (
          <DiscountLabel
            className="PriceDiscount"
            order={discountOrder}
            color={discountColor}
            size={discountSize}>
            {`${prefixContent}${discountFormatted}${suffixContent}`}
          </DiscountLabel>
        ) : null}
        {unitPriceValue ? (
          <UnitPriceLabel
            order={unitOrder}
            className="UnitPrice"
            size={unitsSize}
            color={unitsColor}
            inline={unitsInline}>
            {`${unitsPrefix}${unitsFormatted}${unitsSuffix}`}
          </UnitPriceLabel>
        ) : null}
      </PriceWrapper>
    )
  }
)

Price.displayName = 'Price'
export default Price
